import React, { useRef, useState } from 'react';
import axios from 'axios';
import { allowedExtensions } from '../views/FileManager/constants';
import { ApiRoutes } from '../../src/routes/routeConstants/apiRoutes';
import { UploadFile } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import Notification from '../shared/components/Notification';
import { NotificationTypes } from '../enums/notificationTypes';
import axiosInstance from '../interceptor/axiosInstance';
import { v4 as uuidv4 } from 'uuid';
import { useLegendForm } from '../context/MapLegendContext';
import { inferUploadType } from './useFileUpload';
import { uploadFileInParts, MULTIPART_THRESHOLD_BYTES } from '../shared/utils/s3MultipartUpload';
import { getApiErrorMessage } from '../shared/utils/apiErrorMessage';
import { isCancelError } from '../shared/utils/isCancelError';
import { UploadDestination } from '../enums/uploadDestination';
import { UploadPhase } from '../enums/uploadPhase';

// Mirrors backend collapseRepeatedSegments: the complete gate HeadObject-checks
// collapse(`${folderS3Key}/${s3UniqueFolderName}/${fileName}`), so the PUT key
// must land at that same path. When folderS3Key already ends with the folder id
// (File Manager-style full chain), this strips the accidental duplicate segment.
const collapseRepeatedSegments = (key: string) =>
  key
    .split('/')
    .filter((seg, i, segs) => i === 0 || seg !== segs[i - 1])
    .join('/');

export interface UploadProgress {
  [fileName: string]: number | undefined;
}

export const useMapPortalFileUpload = (currentFolderId: string, updateMapping: () => void) => {
  const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const uploadPhaseRef = useRef<UploadPhase>(UploadPhase.IDLE);
  const [isUploading, setIsUploading] = useState(false);
  const { setS3UniqueFolderName, setDatasetId, folderS3Key } = useLegendForm();

  // Claims the guard before awaited pre-upload work so a second click can't
  // start a duplicate. presignedFileUpload adopts the reservation.
  const reserveUpload = React.useCallback(() => {
    if (uploadPhaseRef.current !== UploadPhase.IDLE) return false;
    uploadPhaseRef.current = UploadPhase.RESERVED;
    setIsUploading(true);
    return true;
  }, []);

  const beginUpload = React.useCallback((controller: AbortController) => {
    abortControllerRef.current = controller;
    uploadPhaseRef.current = UploadPhase.UPLOADING;
    setIsUploading(true);
  }, []);

  // Takes the run's controller so a cancelled run settling late can't release
  // an upload that has already started. Omitted to release unconditionally.
  const endUpload = React.useCallback((controller?: AbortController) => {
    if (controller && abortControllerRef.current !== controller) return;
    abortControllerRef.current = null;
    uploadPhaseRef.current = UploadPhase.IDLE;
    setIsUploading(false);
  }, []);

  const markFinalizing = React.useCallback((controller: AbortController) => {
    if (abortControllerRef.current !== controller) return;
    uploadPhaseRef.current = UploadPhase.FINALIZING;
  }, []);

  // No-op once presignedFileUpload has taken over, so callers can release from
  // a finally without tracking which path they took.
  const releaseIfReserved = React.useCallback(() => {
    if (uploadPhaseRef.current !== UploadPhase.RESERVED) return;
    endUpload();
  }, [endUpload]);

  const isShapefileExtension = (ext: string) => ['dbf', 'shp'].includes(ext);

  let shapefileParts = {
    dbf: false,
    shp: false,
  };

  const checkTifAndTifw = (fileList: UploadFile[]) => {
    const tifFilesExist = fileList.some((file) => file.name.match(/\.(tif|tiff)$/i));
    const tifwFilesExist = fileList.some((file) => file.name.match(/\.tifw$/i));
    if (tifFilesExist !== tifwFilesExist) {
      return true;
    }
    return false;
  };
  const handleFileChange = React.useCallback((event: UploadChangeParam<UploadFile<any>>) => {
    setSelectedFiles(event.fileList);
  }, []);

  const handleFileUpload = (currentDirectoryId: string, onSuccess?: () => void) => {
    if (!selectedFiles || selectedFiles.length === 0) {
      Notification({ message: 'No files selected.', type: NotificationTypes.ERROR });
      return;
    }

    const fileList: UploadFile[] = Array.from(selectedFiles);

    if (checkTifAndTifw(fileList)) {
      Notification({ message: 'Both TIF/TIFF and TIFW files must be uploaded together.', type: NotificationTypes.ERROR });
      return;
    }

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const ext = file.name.split('.').pop()?.toLowerCase();

      if (!ext) {
        console.log('Missing File Extension on file: ', file);
        continue;
      }

      if (!allowedExtensions.has(ext)) {
        const validExtensionsList = Array.from(allowedExtensions)
          .map((validExt) => `• ${validExt}`)
          .join('\n');
        Notification({
          message: `Invalid file type: ${ext}. Please select a valid file. \nValid file types:\n${validExtensionsList}`,
          type: NotificationTypes.ERROR,
        });
        return;
      }

      if (isShapefileExtension(ext)) {
        shapefileParts[ext as keyof typeof shapefileParts] = true;
      }
    }

    const allShapefilePartsSelected = Object.keys(shapefileParts).every((ext) => shapefileParts[ext as keyof typeof shapefileParts]);
    if (Object.values(shapefileParts).some((val) => val) && !allShapefilePartsSelected) {
      Notification({ message: 'Please select all required Shapefile parts (.dbf, .shp).', type: NotificationTypes.ERROR });
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach((file) => {
      if (file.originFileObj) {
        formData.append('submission', file.originFileObj);
      }
    });
    // No folder picked yet falls back to the caller-supplied directory.
    formData.append('currentDirectoryId', currentFolderId || currentDirectoryId || '');
    axiosInstance
      .post('/filemanager/upload-file', formData, {
        onUploadProgress: (progressEvent) => {
          for (let i = 0; i < selectedFiles.length; i++) {
            if (progressEvent.total) {
              let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress((prevProgress: any) => ({
                ...prevProgress,
                [selectedFiles[i].name]: percentCompleted,
              }));
            }
          }
        },
      })
      .then((response) => {
        if (response.data.success) {
          updateMapping();
          setUploadProgress(0);
          setSelectedFiles([]); // Clear files on success
          if (onSuccess) onSuccess();
          Notification({
            message: response?.data?.message,
            type: NotificationTypes.SUCCESS,
          });
        }
      })
      .catch((error) => {
        setUploadProgress(0);
        Notification({
          message: error?.message,
          type: NotificationTypes.ERROR,
        });
      });
  };

  const uploadFileToPresignedUrl = async (
    file: UploadFile,
    presignedUrl: string,
    onUploadComplete?: () => void,
    signal?: AbortSignal,
  ): Promise<boolean> => {
    try {
      const response = await axios.put(presignedUrl, file.originFileObj, {
        signal,
        onUploadProgress: (progressEvent) => {
          // A request that already finished sending can still fire a trailing
          // progress event after abort() — ignore it so a cancelled upload
          // can't write state back and reopen the progress modal.
          if (signal?.aborted || !progressEvent.total) return;
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress((prevProgress: any) => ({
            ...prevProgress,
            [file.name]: percentCompleted,
          }));
        },
      });

      if (signal?.aborted) return false;

      if (response.status === 200) {
        console.log(`File ${file.name} uploaded successfully.`);
        onUploadComplete?.();
        return true;
      } else {
        console.error(`Failed to upload file ${file.name}. Status: ${response.status}`);
        return false;
      }
    } catch (error: any) {
      if (!isCancelError(error)) {
        console.error('Error uploading file to presigned URL:', error);
        Notification({
          message: error?.message ?? 'Upload error',
          type: NotificationTypes.ERROR,
        });
      }
      return false;
    }
  };
  const presignedFileUpload = async (
    folderId: string,
    callback: (value: string) => void,
    reupload: boolean = false,
    onSuccess?: () => void,
    destinationType: UploadDestination = reupload ? UploadDestination.REUPLOAD : UploadDestination.NEW,
    targetFolderS3Key?: string,
  ) => {
    if (!selectedFiles || selectedFiles.length === 0) {
      Notification({ message: 'No files selected.', type: NotificationTypes.ERROR });
      return;
    }

    // RESERVED means this call is the one that claimed the guard.
    const phase = uploadPhaseRef.current;
    if (phase !== UploadPhase.IDLE && phase !== UploadPhase.RESERVED) return;

    const controller = new AbortController();
    beginUpload(controller);

    // Only upload-complete may set this — a failed or cancelled upload must not
    // leave the previous dataset's ID behind for save-layer to send.
    setDatasetId(undefined);

    let completedUploads = 0;
    const totalFiles = selectedFiles.length;

    // New uploads use a generated dataset folder. Existing-folder uploads and
    // reuploads use the selected dataset folder directly.
    const baseName = selectedFiles?.[0]?.name.split('.')[0];
    const uniqueFolderName = `${baseName}-${uuidv4().slice(0, 5)}`;
    const s3FolderName = destinationType === UploadDestination.NEW ? uniqueFolderName : folderId;
    const uploadFolderS3Key = targetFolderS3Key ?? folderS3Key;
    callback(s3FolderName);

    // Show every file at 0% immediately instead of waiting for each file's
    // first network round-trip (presign/multipart-create) to resolve — avoids
    // a perceived delay under slow networks and a list that grows one row at a time.
    setUploadProgress(Object.fromEntries(selectedFiles.map((f) => [f.name, 0])));

    const handleSingleUploadComplete = () => {
      completedUploads += 1;
      console.log(`Upload progress: ${completedUploads}/${totalFiles}`);

      // Register with the backend only after every PUT lands. Success UI waits
      // on that call — bytes in S3 alone are not a finished upload.
      if (completedUploads === totalFiles) {
        // Every file is at 100% in S3 — cancelling from here would orphan them.
        markFinalizing(controller);
        setUploadProgress(0);
        void updateUploadCompletion(s3FolderName, destinationType, folderId, uploadFolderS3Key, onSuccess);
      }
    };

    // Per file: large files go through S3 multipart (parallel parts + resume),
    // small files keep the single presigned PUT. The S3 key is identical either
    // way, so updateUploadCompletion downstream is unchanged.
    const uploadOne = async (file: UploadFile): Promise<boolean> => {
      const key = collapseRepeatedSegments(`${uploadFolderS3Key}/${s3FolderName}/${file.name}`);
      const originFile = file.originFileObj as File | undefined;
      if (!originFile) {
        console.warn(`No file object for ${file.name}`);
        return false;
      }
      try {
        if (controller.signal.aborted) return false;
        if (originFile.size >= MULTIPART_THRESHOLD_BYTES) {
          await uploadFileInParts({
            file: originFile,
            key,
            onProgress: (percent) => {
              if (controller.signal.aborted) return;
              setUploadProgress((prev: any) => ({ ...prev, [file.name]: percent }));
            },
            signal: controller.signal,
          });
          if (controller.signal.aborted) return false;
          handleSingleUploadComplete();
          return true;
        }
        const fileExtension = file.name.split('.')[1];
        const { data } = await axiosInstance.post(ApiRoutes.PRESIGNED_URL, { fileName: key, fileType: fileExtension }, { signal: controller.signal });
        if (controller.signal.aborted) return false;
        return await uploadFileToPresignedUrl(file, data.presignedUrl.presignedUrl, handleSingleUploadComplete, controller.signal);
      } catch (error: any) {
        if (!isCancelError(error)) {
          Notification({
            message: error?.message ?? `Upload failed for ${file.name}`,
            type: NotificationTypes.ERROR,
          });
        }
        return false;
      }
    };

    try {
      const results = await Promise.all(selectedFiles.map(uploadOne));
      // Cancellation is user-initiated, not a failure — no batch error toast.
      if (controller.signal.aborted) {
        endUpload(controller);
        return;
      }
      // Failures already toast per-file; if any PUT failed, complete is never
      // reached (counter never hits totalFiles), so surface a batch-level error.
      if (!results.every(Boolean)) {
        endUpload(controller);
        setUploadProgress(0);
        Notification({
          message: 'One or more files failed to upload.',
          type: NotificationTypes.ERROR,
        });
      }
      // On full success, the guard is released in updateUploadCompletion's
      // finally — it stays held through registration, not just the raw PUTs.
    } catch (error: any) {
      endUpload(controller);
      if (!isCancelError(error)) {
        console.error('Error in presigned file upload:', error);
        setUploadProgress(0);
        Notification({
          message: error?.message ?? 'Presigned upload failed',
          type: NotificationTypes.ERROR,
        });
      }
    }
  };

  const cancelUpload = React.useCallback(() => {
    // Too late to cancel: the bytes are already in S3 and registration is in
    // flight, so honouring this would leave orphaned objects behind.
    if (uploadPhaseRef.current === UploadPhase.FINALIZING) return;
    abortControllerRef.current?.abort();
    setUploadProgress(0);
    endUpload();
  }, [endUpload]);

  // Tell the backend which S3 keys just landed. s3FolderName must be the same
  // segment used in the PUT key above so completion checks find the objects.
  const updateUploadCompletion = async (
    s3FolderName: string,
    destinationType: UploadDestination,
    targetFolderId: string,
    targetFolderS3Key?: string,
    onSuccess?: () => void,
  ) => {
    try {
      const fileNames = selectedFiles.map((file) => file.name);
      const uploadType = inferUploadType(selectedFiles);

      const response = await axiosInstance.post(ApiRoutes.S3_UPLOAD_COMPLETE, {
        // Existing-folder and reupload modes resolve the selected folder by
        // currentFolderId; new mode uses it as the parent of the generated
        // s3FolderName subfolder.
        currentFolderId: targetFolderId,
        s3UniqueFolderName: s3FolderName,
        fileNames,
        folderS3Key: targetFolderS3Key && targetFolderS3Key !== 'null' ? targetFolderS3Key : '',
        reupload: destinationType === UploadDestination.REUPLOAD,
        destinationType,
        // Omitted when unrecognised so the backend falls back to its own detection.
        ...(uploadType && { type: uploadType }),
      });

      // /s3/upload-complete returns { message, folderId, files } — no `success`.
      const files = response.data?.files;
      if (Array.isArray(files) && files.length > 0) {
        setS3UniqueFolderName(response.data.folderId);
        // Keep the dataset identity for the subsequent save-layer request.
        // Raster uploads do not create vector datasets, so clear any prior ID.
        setDatasetId(response.data.datasetId);
        setSelectedFiles([]);
        // Refresh the file-manager listing after the server has committed the
        // replacement. Calling this before upload-complete would race the DB
        // transaction and leave the old file versions visible in the UI.
        await updateMapping();
        onSuccess?.();
        Notification({
          message: response.data?.message ?? 'All files uploaded successfully.',
          type: NotificationTypes.SUCCESS,
        });
      } else {
        Notification({
          message: 'Files uploaded to storage but failed to register.',
          type: NotificationTypes.ERROR,
        });
      }
    } catch (error: unknown) {
      console.error('Error updating upload completion:', error);
      // Prefer the backend's 400 validation wording (missing components, geometry
      // mismatch, etc.) over axios's generic status text.
      const message = getApiErrorMessage(error, 'Upload completion update failed');
      Notification({
        message,
        type: NotificationTypes.ERROR,
      });
    } finally {
      endUpload();
    }
  };

  return {
    selectedFiles,
    setSelectedFiles,
    uploadProgress,
    isUploading,
    handleFileChange,
    handleFileUpload,
    presignedFileUpload,
    cancelUpload,
    reserveUpload,
    releaseIfReserved,
  };
};
